// ─── Is this plan safe to quote from? (plancheck-v1-2026-10-13) ─────────────
//
// Grant, 13 Oct 2026: "if the plan is wrong, it needs to say it's wrong, and
// not just put in numbers."
//
// Until now a scan always ended in numbers. When the geometry engine couldn't
// measure a plan (Pike: angled walls; Paraite: walls don't close) the app
// quietly showed the AI's by-eye figures in the same tiles as measured ones,
// and when the engine measured only part of a house (Working Drawings: the
// bedroom wing, 71 m2 of a ~180 m2 house) nothing said so at all.
//
// One verdict per scan, worked out from the AI's own read of the plan
// (analysis.planCheck), the engine's envelope (tier, coverage, calibration)
// and plain sanity limits:
//
//   stop  - can't read it: not a floor plan, unreadable, no rooms, cut off,
//           or impossible numbers. NO numbers are shown.
//   hold  - read, but not measured from the drawing (angled walls, a scanned
//           image, a photo, the engine didn't answer), or only part of the
//           house was measured, or the plan is an alteration of an existing
//           building (alter-v1-2026-10-23). Numbers stay hidden until the
//           tradie taps "show them anyway", and are marked rough everywhere
//           after.
//   check - measured from the drawing, with specific things to check.
//   ok    - measured from the drawing.
//
// Shared by the server (server.js stores it with the job as
// analysis.planVerdict) and the app (which shows it, and works it out for jobs
// scanned before it existed). CommonJS on purpose: Node requires it, and the
// app's bundler imports it.

var VERSION = "plancheck-v6-2026-10-23";

// Sanity limits for a house or small commercial job. Generous on purpose: a
// "stop" hides everything, so it needs numbers that cannot be right.
var FLOOR_MIN_M2 = 3;            // a single small room still passes
var FLOOR_MAX_M2 = 3000;
var WALLS_PER_FLOOR = [0.5, 8];        // wall m2 per floor m2
var STOP_PER_FLOOR = [0.2, 5];         // square stop lm per floor m2

// Part of the building not measured (see cornercheck.coverage in the engine)
var PARTIAL_SPAN_STRONG_M2 = 15;       // with >= 30% of the measured footprint
var PARTIAL_SPAN_WEAK_M2 = 8;          // with >= 10%
var VISION_RATIO_STRONG = 1.6;         // AI's floor area / measured footprint (with >= 40 m2 between)
var VISION_RATIO_WEAK = 1.3;
var VISION_RATIO_SAME = 1.15;          // the AI agrees with the measured size: walls outside are fences, not house

// A number, or the FIRST number in a string ("279 m2" -> 279; stripping the
// non-digits instead made it 2792).
function num(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  if (typeof v === "string") {
    var m = v.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : null;
  }
  var n = Number(v);
  return isFinite(n) ? n : null;
}
function r0(n) { return Math.round(n); }
function txt(s, max) {
  if (typeof s !== "string") return "";
  s = s.replace(/\s+/g, " ").trim();
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}
function field(env, k) {
  var f = env && env.fields && env.fields[k];
  return (f && typeof f === "object") ? f : {};
}

// storeys-v2-2026-10-23: a multi-storey set where no floor could be
// measured says each floor's own reason. Fowler's note is "none of the 2
// floor plans could be measured (Ground floor: non-orthogonal…; First floor:
// wall model did not enclose…)", and the first pattern to match used to
// speak for the whole house ("angled walls").
var FLOOR_WHY = [
  [/flattened|raster|no usable vectors/, function (r) { return cap(r) + "'s sheet is a scan, not the drawing file"; }],
  [/non-orthogonal|angled/, function (r) { return cap(r) + " has angled walls, which PlanBoss can't measure yet"; }],
  [/scale could not/, function (r) { return "No drawing scale was found for " + r; }],
  [/did not enclose|footprint|plausible floor/, function (r) { return cap(r) + "'s walls didn't trace into a closed building"; }],
  [/dimension sheet/, function (r) { return cap(r) + "'s dimensions sheet wasn't found"; }],
  [/neither a chain grid nor filled walls/, function (r) { return cap(r) + "'s walls are drawn in a way PlanBoss can't trace yet"; }]
];
function floorsWhy(n) {
  var m = n.match(/none of the (\d+) floor plans could be measured \((.*)\)\s*$/);
  if (!m) return null;
  var parts = m[2].split(/;\s*(?=[^;:()]{1,40}:)/).map(function (p) {
    var i = p.indexOf(":");
    if (i < 0) return null;
    var ref = floorRef(p.slice(0, i).trim()), why = p.slice(i + 1);
    for (var k = 0; k < FLOOR_WHY.length; k++) if (FLOOR_WHY[k][0].test(why)) return FLOOR_WHY[k][1](ref);
    return cap(ref) + " couldn't be measured";
  }).filter(Boolean);
  if (!parts.length) return null;
  return "PlanBoss couldn't measure " + (m[1] === "2" ? "either floor" : "any of the " + m[1] + " floors") +
    " from the drawing. " + parts.join(". ") + ".";
}

// Why the engine couldn't measure it, in a tradie's words.
function engineWhy(note) {
  var n = String(note || "").toLowerCase();
  var perFloor = floorsWhy(n);
  if (perFloor) return perFloor;
  if (/flattened|raster|no usable vectors/.test(n)) {
    return "This PDF is a scan or picture of the plan, not the drawing file, so nothing on it can be measured. " +
      "Ask the designer for the PDF exported straight from their drawing software.";
  }
  if (/non-orthogonal|angled/.test(n)) return "This plan has angled walls, which PlanBoss can't measure yet.";
  if (/scale could not/.test(n)) return "No drawing scale could be found on this plan, so nothing on it could be measured.";
  if (/did not enclose|footprint/.test(n)) return "PlanBoss couldn't trace the walls of this plan into a closed building.";
  if (/dimension sheet/.test(n)) return "PlanBoss couldn't find this plan's dimensions sheet.";
  if (/neither a chain grid nor filled walls/.test(n)) return "The walls on this plan are drawn in a way PlanBoss can't trace yet.";
  return "PlanBoss couldn't measure the walls on this plan.";
}

// What the engine did with this plan.
function classifyGeometry(env, isPdf) {
  if (!isPdf) return { measured: false, kind: "image" };
  if (!env || typeof env !== "object") return { measured: false, kind: "engine_down" };
  var tier = env.tier, version = String(env.version || env.engine_version || "");
  var walls = num(field(env, "walls_m2").value), sq = num(field(env, "square_stop_lm").value);
  if (env.v2_tier || /^C/.test(String(tier || ""))) {
    // v2 couldn't measure it; v1's printed-room-size formula may still have
    if (walls !== null && sq !== null) return { measured: true, kind: "v1", tier: tier, version: version,
      note: env.v2_tier_note || env.tier_note || "" };
    return { measured: false, kind: "not_measured", tier: env.v2_tier || tier, version: env.v2_version || version,
      note: env.v2_tier_note || env.tier_note || "" };
  }
  if (walls === null || sq === null) {
    return { measured: false, kind: "not_measured", tier: tier, version: version, note: env.tier_note || "" };
  }
  return { measured: true, kind: /^metrics-v1/.test(version) ? "v1" : "v2", tier: tier, version: version };
}

// "the first floor", but "Level 1": a numbered level keeps its capital and
// takes no "the". cap() starts a sentence with it.
function floorRef(name) {
  var n = txt(String(name || "a floor"), 40);
  return /^level\s*\d/i.test(n) ? n.replace(/^level/i, "Level") : "the " + n.toLowerCase();
}
function cap(s) { s = String(s || ""); return s.charAt(0).toUpperCase() + s.slice(1); }
function visionFloor(a) {
  var f = num(a && a.floorArea);
  if (f !== null && f > 0) return f;
  var rooms = a && Array.isArray(a.rooms) ? a.rooms : [];
  var s = 0, any = false;
  rooms.forEach(function (r) { var v = num(r && r.floorM2); if (v !== null && v > 0) { s += v; any = true; } });
  return any ? s : null;
}
function visionSum(a, key) {
  var rooms = a && Array.isArray(a.rooms) ? a.rooms : [];
  var s = 0, any = false;
  rooms.forEach(function (r) { var v = num(r && r[key]); if (v !== null) { s += v; any = true; } });
  return any ? s : null;
}

// alter-v1-2026-10-23: the engine's reading of the drawing set (env.scope,
// planboss_engine/scope.py): is it an alteration of an existing building?
// Said back to the tradie in a few words, so they can see why.
function alterationSaid(scope) {
  var ev = scope && Array.isArray(scope.evidence) ? scope.evidence.filter(function (e) { return e && typeof e === "object"; }) : [];
  var title = ev.filter(function (e) { return e.sign === "title"; })[0];
  if (title && title.text) return "the drawings are titled \u201c" + txt(String(title.text), 60) + "\u201d";
  var plan = ev.filter(function (e) { return e.sign === "existing_plan"; })[0];
  if (plan && plan.text) {
    var t = txt(String(plan.text), 50);
    return "the set has " + (/^[aeiou]/i.test(t) ? "an" : "a") + " \u201c" + t + "\u201d sheet";
  }
  if (ev.some(function (e) { return e.sign === "key"; })) return "its key shows existing walls staying, with walls added or removed";
  return "the drawings describe work on an existing building";
}
// The AI's own read (planCheck.scope / planCheck.newWork, asked for since
// alter-v1): "alteration" or "new build", and what the new work is.
function aiAlteration(pc) {
  return !!(pc && typeof pc.scope === "string" && /alter|renovat|addition|extension/i.test(pc.scope) && !/new ?build/i.test(pc.scope));
}

// input: { analysis, geometry, mediaType, fileName, truncated, parseMethod, legacy }
function planVerdict(input) {
  try { return judge(input || {}); }
  catch (e) {
    return { v: 1, version: VERSION, verdict: "check", kind: "internal", measured: false,
      headline: "Check these numbers against the plan",
      reasons: [{ code: "internal", level: "warn",
        text: "PlanBoss couldn't run its plan checks on this scan, so check every number against the plan before you quote." }] };
  }
}

function judge(input) {
  var a = (input.analysis && typeof input.analysis === "object") ? input.analysis : {};
  var env = input.geometry !== undefined ? input.geometry : a.geometry;
  var mt = String(input.mediaType || "");
  var fname = String(input.fileName || "");
  var isPdf = mt ? /pdf/i.test(mt)
    : (/\.pdf$/i.test(fname) || !!env || (input.legacy && !/\.(png|jpe?g|gif|webp|heic)$/i.test(fname)));
  var geo = classifyGeometry(env, isPdf);
  var pc = (a.planCheck && typeof a.planCheck === "object") ? a.planCheck : null;
  var problems = pc && Array.isArray(pc.problems)
    ? pc.problems.map(function (p) { return txt(p, 140); }).filter(Boolean).slice(0, 4) : [];
  var reasons = [];
  var add = function (code, level, text) { reasons.push({ code: code, level: level, text: text }); };
  // measuredAs: alter-v2 (review, 23 Oct) — an alteration whose numbers came
  // from the printed room sizes (v1) is not "the whole building measured".
  var out = function (verdict, kind, headline, measuredAs) {
    return { v: 1, version: VERSION, verdict: verdict, kind: kind || null, headline: headline,
      measured: measuredAs === undefined ? !!geo.measured : !!measuredAs,
      engine: { kind: geo.kind, tier: geo.tier || null, version: geo.version || null },
      reasons: reasons, legacy: !!input.legacy };
  };

  // The AI's answer was cut off: either it hit its output limit, or the JSON
  // had to be repaired by cutting it short. Rooms at the end are missing.
  var cutOff = !!input.truncated || input.parseMethod === "truncation-recovered";
  var roomsWithArea = (Array.isArray(a.rooms) ? a.rooms : []).filter(function (r) {
    var v = num(r && r.floorM2); return v !== null && v > 0;
  });
  var vFloor = visionFloor(a);
  var gFloor = geo.measured ? num(field(env, "floor_m2").value) : null;
  var gFoot = geo.measured ? num(field(env, "footprint_m2").value) : null;
  var notFloorPlan = !!(pc && pc.isFloorPlan === false);
  var unreadable = !!(pc && pc.readable === false);
  var problemNote = problems.length ? " The AI noted: " + problems.join("; ") + "." : "";

  // ── STOP: can't read it ──────────────────────────────────────────────────
  if (!geo.measured) {
    if (notFloorPlan) {
      add("not_floor_plan", "stop", (pc.whatItIs ? "This looks like " + txt(pc.whatItIs, 100).replace(/\.$/, "") + ", not a floor plan." :
        "This doesn't look like a floor plan.") + " Upload the floor plan — the PDF from the architect or designer works best.");
      return out("stop", "not_floor_plan", "This doesn't look like a floor plan");
    }
    if (unreadable) {
      add("unreadable", "stop", "The plan is too unclear to read reliably." + problemNote +
        " Upload a sharper copy — ideally the PDF from the architect or designer.");
      return out("stop", "unreadable", "This plan is too unclear to read");
    }
    if (!roomsWithArea.length) {
      add("no_rooms", "stop", "No rooms with sizes could be read from this plan." + problemNote +
        " Make sure the file includes the floor plan sheet.");
      return out("stop", "no_rooms", "We couldn't find any rooms to measure");
    }
  }

  // impossible numbers, judged on what would be shown. Like with like: the
  // engine's walls against the engine's floor; the AI's room-by-room walls
  // against the AI's room-by-room floor (its overall floorArea can cover
  // rooms its list left out, which isn't impossible, just incomplete).
  var floor = geo.measured ? gFloor : visionSum(a, "floorM2");
  var walls = geo.measured ? num(field(env, "walls_m2").value) : visionSum(a, "wallM2Net");
  var square = geo.measured ? num(field(env, "square_stop_lm").value) : visionSum(a, "perimeterLm");
  var why = [];
  var aiFloor = num(a.floorArea);
  if (aiFloor !== null && aiFloor > 0 && (aiFloor < FLOOR_MIN_M2 || aiFloor > FLOOR_MAX_M2) && !geo.measured) {
    why.push(r0(aiFloor * 10) / 10 + " m² of floor");
  } else if (floor !== null && (floor < FLOOR_MIN_M2 || floor > FLOOR_MAX_M2)) why.push(r0(floor * 10) / 10 + " m² of floor");
  if (floor && floor >= FLOOR_MIN_M2 && walls !== null && walls > 0) {
    var wr = walls / floor;
    if (wr < WALLS_PER_FLOOR[0] || wr > WALLS_PER_FLOOR[1]) why.push(r0(walls) + " m² of walls on " + r0(floor) + " m² of floor");
  }
  if (floor && floor >= FLOOR_MIN_M2 && square !== null && square > 0) {
    var sr = square / floor;
    if (sr < STOP_PER_FLOOR[0] || sr > STOP_PER_FLOOR[1]) why.push(r0(square) + " m of square stop on " + r0(floor) + " m² of floor");
  }
  if (why.length) {
    add("impossible", "stop", "The measurements came out impossible (" + why.join(", ") + "), so this plan wasn't read properly." +
      (geo.measured ? " The drawing's scale may have been misread." : "") + " Check it's the right file, or try the PDF from the designer.");
    return out("stop", "impossible", "The numbers came out impossible");
  }

  // ── an alteration of an existing building (alter-v1/v2-2026-10-23) ───────
  // PlanBoss measures whole buildings. On an alteration only part of the
  // building is new work (Advantage: an accessible WC/shower and a hallway
  // wall; Pinnacle: new bathrooms in an existing house), so the whole-
  // building take-off would price walls and ceilings nobody is touching.
  // alter-v2 (independent review, 23 Oct): it's said ALONGSIDE every other
  // check, never instead of one — a part-measured alteration is still held as
  // part-measured (its numbers short), and only a complete measurement is
  // "the whole building".
  var scope = env && env.scope && typeof env.scope === "object" ? env.scope : null;
  var aiNewWork = pc && typeof pc.newWork === "string" ? txt(pc.newWork, 140).replace(/\.$/, "") : "";
  var aiAlter = aiAlteration(pc);
  var isAlter = !!(scope && scope.alteration === true);
  var workNote = aiNewWork ? " The AI reading the plan says the new work is: " + aiNewWork + "." : "";
  // alter-v3 (second review): the AI's "new build" never un-holds what the
  // drawings mark as an alteration (its scope read is unproven; an un-held
  // alteration quotes the whole building as measured). It's said instead,
  // so a tradie looking at a new build knows why it was held.
  if (isAlter && pc && typeof pc.scope === "string" && /new ?build/i.test(pc.scope)) {
    workNote += " (The AI reading the plan thinks this is a new build. If it is, the numbers are the whole job — check the drawings.)";
  }
  var altSaid = isAlter ? alterationSaid(scope) : "";
  var alsoAlter = function () {
    if (isAlter) add("alteration", "hold", "This plan is also an alteration of an existing building (" + altSaid +
      "), so only part of it is new work. Take off just the rooms being worked on." + workNote);
  };
  // a hold on the measured path: says it's an alteration too
  var holdOut = function (kind, headline) { alsoAlter(); return out("hold", kind, headline); };

  // ── HOLD: read, but not measured from the drawing ─────────────────────────
  if (!geo.measured) {
    var holdWhy = geo.kind === "image"
      ? "Photos and images can't be measured, only read by eye. Upload the plan as a PDF for measured numbers."
      : geo.kind === "engine_down"
        ? (input.legacy
          ? "This job was scanned without the measuring engine, so nothing on it was measured from the drawing. Scan the plan again for measured numbers."
          : "The measuring engine didn't answer (it may have been busy or starting up, or it couldn't open this file). Try the scan once more; if it happens again, this file can't be measured as it is.")
        : engineWhy(geo.note);
    if (isAlter) {
      // alter-v2: the real reason it couldn't be measured is kept (review #10)
      add("alteration", "hold", "This plan is an alteration of an existing building (" + altSaid + "), so only part of it is new work. " +
        holdWhy + " The numbers would be the AI reading the plan by eye — they can be well out, and they may include rooms nobody is touching." +
        (cutOff ? " The AI's read was also cut off part-way, so some rooms are missing." : "") + workNote + problemNote);
      return out("hold", "alteration", "This plan is an alteration");
    }
    add(geo.kind === "image" ? "image" : geo.kind === "engine_down" ? "engine_down" : "not_measured", "hold",
      holdWhy + " The numbers would be the AI reading the plan by eye, and those can be well out — we've seen 30% and more." +
      (cutOff ? " The AI's read was also cut off part-way, so some rooms are missing and the totals would come out short." : "") +
      (aiAlter ? " The AI also reads this as an alteration of an existing building" + (aiNewWork ? " (new work: " + aiNewWork + ")" : "") +
        ", so its numbers may include rooms nobody is touching." : "") + problemNote);
    return out("hold", geo.kind === "image" ? "image" : geo.kind === "engine_down" ? "engine_down" : "not_measured",
      geo.kind === "image" ? "A photo can't be measured" : "Couldn't measure this plan from the drawing");
  }

  // measured — did it take in the whole building?
  // 1. More than one floor level. The engine measures ONE plan sheet, so on
  //    a two-storey house the other level is missing from every number.
  var lv = env && env.levels && typeof env.levels === "object" ? env.levels : null;
  var aiLevels = pc ? num(pc.levels) : null;
  // every-storey-v1-2026-10-14: the engine measures every floor's plan
  // sheet it can find (env.level_results). All of them measured = the whole
  // house; fewer than the floors the drawings (or the AI) show = held.
  var lr = env && Array.isArray(env.level_results)
    ? env.level_results.filter(function (x) { return x && typeof x === "object"; }) : null;
  var multiMeasured = null;               // the floors measured, when more than one
  if (lr && lr.length >= 2) {
    var done = lr.filter(function (x) { return x.measured; });
    var expected = Math.max(lr.length, aiLevels !== null ? Math.min(aiLevels, 12) : 0);
    var nm = function (x) { return txt(String(x.name || "a floor"), 40); };
    if (done.length < expected) {
      var missing = lr.filter(function (x) { return !x.measured; }).map(function (x) { return floorRef(x.name); });
      var unseen = expected - lr.length;
      add("multi_level", "hold", "PlanBoss measured " + done.length + " of " + expected + " floors" +
        (done.length ? " (" + done.map(nm).join(", ") + ")" : "") + ". " +
        (missing.length ? cap(missing.join(" and ")) + " plan" + (missing.length > 1 ? "s" : "") + " couldn't be measured" : "") +
        (unseen > 0 ? (missing.length ? ", and " : "") + unseen + " more floor" + (unseen > 1 ? "s" : "") +
          " the AI saw on the drawings " + (unseen > 1 ? "have" : "has") + " no plan sheet PlanBoss could find" : "") +
        ". Those floors' walls, ceilings, stopping and corners aren't in these numbers.");
      return holdOut("partial", "Only " + done.length + " of " + expected + " floors were measured");
    }
    // More floors measured than the drawings show (review, 14 Oct): one floor
    // drawn twice under two names, or a sheet that isn't a floor plan. The
    // numbers could be double — held until the tradie has looked.
    if (aiLevels !== null && aiLevels >= 1 && done.length > aiLevels) {
      add("levels_extra", "hold", "PlanBoss measured " + done.length + " floor plans (" + done.map(nm).join(", ") + "), but the AI reading the drawings counted " +
        aiLevels + " floor" + (aiLevels > 1 ? "s" : "") + ". If one of those is the same floor drawn twice, or isn't a floor plan, every number here is too big. " +
        "Look at each floor's corner picture before you use these numbers.");
      return holdOut("levels", "More floors measured than the drawings show");
    }
    // A sheet with two floors' plans side by side, as well as the floors
    // that were measured: something may be on that sheet and nowhere else.
    if (lv && Array.isArray(lv.same_sheet) && lv.same_sheet.length) {
      add("multi_level", "hold", "One sheet carries two floors' plans side by side, and PlanBoss can't split those yet, so these numbers may be missing part of the house. " +
        "Upload the set with each floor's plan on its own page for measured numbers.");
      return holdOut("partial", "Two floors are drawn on one sheet");
    }
    // A floor only the AI picked that measures almost the same as another
    // floor may be that floor's dimension sheet, not its own plan (review).
    var look = lv && Array.isArray(lv.ai_lookalike) ? lv.ai_lookalike.filter(function (x) { return x && x.level; }) : [];
    if (look.length) {
      add("levels_lookalike", "hold", look.map(function (x) {
        return cap(floorRef(x.level)) + " plan was picked out by the AI's read of the drawings and measures almost the same as " +
          floorRef(x.like || "other floor");
      }).join("; ") + ". It may be the same floor's dimension or framing sheet rather than its own plan, which would double those numbers. " +
        "Look at each floor's corner picture before you use these numbers.");
      return holdOut("levels", "Check the floors before quoting");
    }
    multiMeasured = done;
    add("levels_measured", "info", "Measured all " + done.length + " floors: " + done.map(nm).join(", ") + ".");
    var byAi = done.filter(function (x) { return x.source === "ai"; });
    if (byAi.length) {
      add("levels_by_ai", "warn", cap(byAi.map(function (x) { return floorRef(x.name); }).join(" and ")) +
        " plan sheet" + (byAi.length > 1 ? "s were" : " was") + " picked out by the AI's read of the drawings (the sheet title isn't readable text). " +
        "Check the corner picture for " + (byAi.length > 1 ? "those floors" : "that floor") + " shows the right plan.");
    }
  } else {
    var multi = !!(lv && lv.multi) || (aiLevels !== null && aiLevels >= 2);
    if (multi) {
      var names = lv && Array.isArray(lv.names) && lv.names.length >= 2
        ? lv.names.slice(0, 4).map(function (x) { return String(x).toLowerCase(); }).join(" and ") + " floor plans"
        : (aiLevels >= 2 ? aiLevels + " floor levels" : "more than one floor level");
      // every-storey-v1-2026-10-14: two floors' plans side by side on one
      // sheet can't be told apart yet — say that, not "only one sheet".
      if (lv && Array.isArray(lv.same_sheet) && lv.same_sheet.length) {
        add("multi_level", "hold", "The " + names + " are drawn on the same sheet, and PlanBoss can't measure two floors from one sheet yet, " +
          "so these numbers can't be trusted as the whole house. Upload the set with each floor's plan on its own page for measured numbers.");
        return holdOut("partial", "Two floors are drawn on one sheet");
      }
      // The engine found several floors but couldn't measure them, and the
      // numbers came from elsewhere (the old printed-room-sizes engine).
      var found = lv && Array.isArray(lv.plans) ? lv.plans.filter(function (x) { return x && typeof x === "object"; }) : [];
      if (found.length >= 2) {
        add("multi_level", "hold", "PlanBoss found plans for " + found.length + " floors (" +
          found.slice(0, 6).map(function (x) { return txt(String(x.name || "a floor"), 40); }).join(", ") +
          ") but couldn't measure every floor from the drawing, so these numbers don't cover the whole house.");
        return holdOut("partial", "Not every floor of this house was measured");
      }
      add("multi_level", "hold", "The drawings show " + names + ", but PlanBoss could find only one floor's plan sheet to measure. " +
        "The other level's walls, ceilings, stopping and corners aren't in these numbers.");
      return holdOut("partial", "Only one floor of this house was measured");
    }
  }
  // A floor plan found twice and counted once: right when it's one floor
  // drawn twice; if they're really two floors that look the same, one is
  // missing — say so (review, 14 Oct).
  var merged = lv && Array.isArray(lv.notes) ? lv.notes.filter(function (n) { return /counted once/.test(String(n)); }) : [];
  if (merged.length) {
    add("levels_merged", "warn", "PlanBoss found the same floor plan twice and counted it once (" + merged.slice(0, 2).map(function (n) { return txt(String(n), 160); }).join("; ") +
      "). If they're really two floors that look the same, one of them is missing from these numbers.");
  }
  // 2. Walls on the plan outside the measured building, and/or the AI reading
  //    a much bigger house than was measured.
  var cov = env && env.coverage && typeof env.coverage === "object" ? env.coverage : null;
  var foot = (cov && num(cov.footprint_m2)) || gFoot || 0;
  var ratio = (vFloor && foot) ? vFloor / foot : null;
  // Walls outside the measured building, judged floor by floor: a wing
  // missing from one floor mustn't be diluted by the others.
  var covLevels = multiMeasured
    ? multiMeasured.map(function (x) {
        var c = x.coverage && typeof x.coverage === "object" ? x.coverage : {};
        var f = num(c.footprint_m2) || (x.fields && x.fields.footprint_m2 && num(x.fields.footprint_m2.value)) || 0;
        return { name: x.name, span: num(c.outside_span_m2) || 0, foot: f };
      })
    : [{ name: null, span: cov ? (num(cov.outside_span_m2) || 0) : 0, foot: foot }];
  // the floor with the most of its plan left out (by share, once there's
  // enough of it to matter)
  var badness = function (c) {
    return c.span >= PARTIAL_SPAN_WEAK_M2 ? 1 + (c.foot ? c.span / c.foot : 1) : c.span / 1000;
  };
  var worst = covLevels.reduce(function (a, b) { return badness(b) > badness(a) ? b : a; }, covLevels[0]);
  var span = worst.span;
  var spanShare = worst.foot ? worst.span / worst.foot : 0;
  var onFloor = multiMeasured && worst.name ? " on " + floorRef(worst.name) : "";
  // If the AI's read of the floor area matches what was measured, walls
  // outside are fences, garden or retaining walls rather than missing rooms.
  // Matches = within 15% either way, from the AI's own floor-area figure (not
  // a room list that may be incomplete) and an AI read that wasn't cut off.
  var aiFloorArea = num(a.floorArea);
  var aiAgrees = ratio !== null && ratio >= 1 / VISION_RATIO_SAME && ratio < VISION_RATIO_SAME &&
    !cutOff && aiFloorArea !== null && aiFloorArea > 0;
  var strongPartial = (span >= PARTIAL_SPAN_STRONG_M2 && spanShare >= 0.3 && !aiAgrees) ||
    (span >= PARTIAL_SPAN_WEAK_M2 && ratio !== null && ratio >= VISION_RATIO_WEAK) ||
    (ratio !== null && ratio >= VISION_RATIO_STRONG && vFloor - foot >= 40);
  var weakPartial = !strongPartial && ((span >= PARTIAL_SPAN_WEAK_M2 && spanShare >= 0.1) ||
    (ratio !== null && ratio >= VISION_RATIO_WEAK));
  // sentinel-v1 guard: the plan prints a floor area well over what was traced,
  // with nothing drawn outside to colour red (review #7: don't promise red)
  var pShort = !multiMeasured && cov && cov.printed_floor_short && typeof cov.printed_floor_short === "object" ? cov.printed_floor_short : null;
  var noRed = !(cov && Array.isArray(cov.outside_regions) && cov.outside_regions.length);
  if (strongPartial) {
    add("partial", "hold", "PlanBoss measured about " + r0(gFloor || foot) + " m² of floor" +
      (multiMeasured ? " over " + multiMeasured.length + " floors" : "") +
      (ratio !== null && ratio >= VISION_RATIO_WEAK ? ", but the plan looks closer to " + r0(vFloor) + " m²" : "") +
      ". Part of the house wasn't measured" + onFloor + ", so walls, ceilings and stopping would come out short. " +
      (pShort && noRed && num(pShort.printed_m2)
        ? "The plan prints " + r0(num(pShort.printed_m2)) + " m²" + (pShort.label ? " (" + txt(String(pShort.label), 60) + ")" : "") +
          ", more than the building PlanBoss traced. The check picture can't show which part is missing, so compare its green outline with the plan."
        : "The check picture shows what was measured (green) and what wasn't (red)."));
    return holdOut("partial", "Part of this house wasn't measured");
  }
  if (weakPartial) {
    if (pShort && noRed && num(pShort.printed_m2)) {
      // second review #9: nothing is drawn outside, so don't say there is
      add("partial_maybe", "warn", "The plan prints " + r0(num(pShort.printed_m2)) + " m² of floor" + (pShort.label ? " (" + txt(String(pShort.label), 60) + ")" : "") +
        ", more than the " + r0(gFloor || foot) + " m² PlanBoss measured" + onFloor + ". Check the green outline on the corner picture covers the whole house.");
    } else if (ratio !== null && ratio >= VISION_RATIO_WEAK) {
      add("partial_maybe", "warn", "PlanBoss measured about " + r0(foot) + " m² of building, but the AI's read of the plan is about " + r0(vFloor) +
        " m². There may be another floor or wing on a different sheet that wasn't measured — check the plan before quoting.");
    } else {
      add("partial_maybe", "warn", (aiAgrees
        ? "There are walls on the plan" + onFloor + " outside the measured building (fences, garden walls, or part of the house that wasn't measured?)"
        : "Part of the plan" + onFloor + " may not have been measured") + ". Check the green outline on the corner picture covers the whole house.");
    }
  }
  // 3. The other way round: the drawing measured much BIGGER than the AI
  //    reads the plan, with no printed area to settle it — a misread scale.
  var acChk = env && env.calibration && env.calibration.printed_area_check;
  // On a multi-storey job each floor has its own scale check: the printed
  // area only vouches for the scale when every floor's agrees (review).
  var acAll = multiMeasured
    ? multiMeasured.map(function (x) { return x.printed_area_check && typeof x.printed_area_check === "object" ? x.printed_area_check : null; })
    : [acChk];
  var scaleVouched = acAll.length > 0 && acAll.every(function (c) { return c && c.agrees; });
  if (vFloor && foot && foot / vFloor >= VISION_RATIO_STRONG && foot - vFloor >= 40 && !scaleVouched) {
    add("bigger_than_ai", "warn", "PlanBoss measured about " + r0(foot) + " m² of building, but the AI's read of the plan is about " +
      r0(vFloor) + " m². Check one room's size against the plan — if the drawing's scale was misread, every number is out.");
  }
  // 4. What the AI reading the plan flagged, even though the drawing measured.
  //    Anything that sounds like part of the plan is missing or unreadable is
  //    a warning; general caveats about its own read are a note.
  // Round 3 of the review: "shows the ground floor only", "upper level drawn
  // on A1.02, not read", "living wing appears unmeasured" are the notes that
  // matter most when the level titles weren't in the file (a set cut down to
  // one floor page), so a part of the building + a "not there" word within
  // the same sentence counts. Bare "floor" isn't a part ("floor finishes not
  // shown" is a caveat); "ground floor", "floor plan", "wing" are.
  // A sheet number ("A1.03") doesn't end the sentence. A garage only counts
  // straight after it ("garage not drawn", not "garage door size not
  // shown"); "Level 1 RL" is a height, not a floor.
  var SENT = "(?:[^.;]|\\.(?=\\d))";
  // ...and the "not there" word must be about the floor or wing itself, not a
  // detail of it ("first floor joist sizes not shown" is a caveat).
  var DETAIL = "(?:joists?|slabs?|details?|heights?|ceilings?|studs?|sizes?|finish(?:es)?|framing|bracing|beams?|lintels?|" +
    "dimensions?|truss(?:es)?|rafters?|insulation|plumbing|electrical|windows?|doors?|cladding|roof|stairs?|rl\\b|ffl\\b)";
  var NEAR = "(?:(?!" + DETAIL + ")" + SENT + ")";
  var PART = "(?:floor ?plans?|floor layout|(?:ground|first|second|third|upper|lower|top|other) (?:floor|level|storey)s?|" +
    "level \\d(?!\\s*(?:rl|ffl|height|datum))|storey|wing(?!\\s*walls?)|extension|part of the (?:house|plan|building)|(?:the )?rest of the (?:house|plan|building))";
  var SERIOUS = new RegExp([
    "cut ?off", "cropped", "truncated",
    "(?:plan|page|sheet|drawing|wing|rooms?|walls?|level|floor|storey|part of the (?:house|plan|building))\\s+(?:is\\s+|are\\s+)?(?:missing|not (?:shown|included|provided|on this))",
    "missing (?:rooms?|walls?|wing|page|sheet|level|floor|storey|part)",
    "incomplete (?:plan|drawing|floor ?plan|set)",
    "(?:on|in) (?:another|a separate|a different) (?:sheet|page|drawing)",
    "(?:first|upper|second|ground) (?:floor|level|storey)\\s+(?:plan\\s+)?(?:is\\s+)?(?:not|missing)",
    PART + NEAR + "{0,45}?\\b(?:not (?:measured|read|drawn|shown|included|provided|in this)|unmeasured|missing|omitted|absent)",
    "garage (?:is |was )?(?:not (?:measured|read|drawn|shown|included)|unmeasured|missing|omitted)",
    PART + SENT + "{0,12}?\\bonly\\b",
    "\\bonly\\b" + SENT + "{0,20}?\\b(?:part|one|half|some|the (?:ground|first|upper|lower))\\b" + SENT + "{0,25}?\\b(?:house|building|plan|floor|level|storey|wing)",
    "blurr?(?:y|ed)", "illegible", "unreadable", "low[- ]resolution",
    "(?:can'?t|cannot|could not|couldn'?t) (?:read|see|make out)",
    "wrong (?:plan|sheet|page|drawing)", "not a floor ?plan"].join("|"), "i");
  if (unreadable) add("ai_unreadable", "warn", "The AI found parts of this plan hard to read." + (problems.length ? " It noted: " + problems.join("; ") + "." : ""));
  else if (problems.length) {
    var serious = problems.filter(function (x) { return SERIOUS.test(x); });
    if (serious.length) add("ai_problems", "warn", "The AI reading the plan noted: " + problems.join("; ") + ".");
    else add("ai_notes", "info", "The AI reading the plan noted: " + problems.join("; ") + ".");
  }

  // ── CHECK: measured, with things to look at ──────────────────────────────
  if (aiAlter && !isAlter) {
    add("ai_alteration", "warn", "The AI reading the plan says this is an alteration of an existing building" + (aiNewWork ? " (new work: " + aiNewWork + ")" : "") +
      ". PlanBoss measured the whole building, so if only part of it is new work, take off just those rooms before you quote.");
  }
  if (notFloorPlan) add("not_floor_plan_soft", "warn", "The AI reader didn't see a floor plan in this file, though the drawing measured as one. Make sure it's the plan you meant.");
  if (geo.kind === "v1") {
    add("v1", "warn", "Walls and square stop come from the room sizes printed on the plan — the drawing itself couldn't be traced (" +
      engineWhy(geo.note).replace(/\.$/, "") + "). External corners are the AI's estimate.");
  }
  var cal = env && env.calibration;
  var ac = cal && cal.printed_area_check;
  if (multiMeasured) {
    multiMeasured.forEach(function (x) {
      var c = x.printed_area_check;
      if (c && c.agrees === false && num(c.printed_area_m2) && num(c.implied_area_m2)) {
        add("area_mismatch", "warn", cap(floorRef(x.name)) + " measured " + r0(num(c.implied_area_m2)) +
          " m², but the plan prints " + r0(num(c.printed_area_m2)) + " m². Check that floor's scale before you quote.");
      }
    });
  } else if (ac && ac.agrees === false && num(ac.printed_area_m2) && num(ac.implied_area_m2)) {
    add("area_mismatch", "warn", "The building measured " + r0(num(ac.implied_area_m2)) + " m², but the plan prints " +
      r0(num(ac.printed_area_m2)) + " m². Check the scale before you quote.");
  }
  var wallsF = field(env, "walls_m2");
  var grossOn = String(wallsF.method || "").match(/GROSS on ([^(]+)\(/);
  if (wallsF.value != null && multiMeasured && grossOn && field(env, "openings_m2").value) {
    add("gross_walls", "warn", "Window and door openings couldn't be read on " + grossOn[1].trim().split(/,\s*/).map(floorRef).join(" and ") +
      ", so the walls there are gross — nothing was deducted on " + (/,/.test(grossOn[1]) ? "those floors" : "that floor") + ".");
  } else if (wallsF.value != null && (/GROSS/.test(String(wallsF.method || "")) || !field(env, "openings_m2").value)) {
    add("gross_walls", "warn", "Window and door openings couldn't be read, so the wall area is gross — nothing has been deducted.");
  }
  // f6-v2 (second review): openings read from sizes printed without marks
  var opnInfo = env && env.openings && typeof env.openings === "object" ? env.openings : null;
  var nUnmarked = opnInfo ? num(opnInfo.n_unmarked_sizes) : null;
  if (nUnmarked && wallsF.value != null) {
    add("openings_unmarked", "warn", "Window and door openings were read from " + nUnmarked + " sizes printed beside the walls with no window or door marks. " +
      "Check each one is a window or door (not a deck, carport or pergola size) before you quote.");
  }
  if (env && env.ceilings && env.ceilings.uniform_stud_unsafe) {
    add("stud_varies", "warn", "The plan says the stud height varies. The wall area uses one height for every room.");
  }
  if (cutOff) {
    add("truncated_soft", "warn", "The AI's room list was cut short, so the materials list may be missing rooms. The measurements are from the drawing.");
  }
  // The engine said some of its checks couldn't run (a file missing from a
  // deploy): never a silent "ok".
  if (env && Array.isArray(env.engine_warnings) && env.engine_warnings.length) {
    add("engine_checks_off", "warn", "PlanBoss's checks for unmeasured parts of the house didn't run on this scan (a server fault), so check the numbers cover the whole house.");
  }
  // macritchie-v1-2026-10-23: the engine flags an external corner count that
  // can't be right (Mac Ritchie: no doors found, 69 corners against 17).
  var extSus = field(env, "external_corners_lm").suspect;
  if (extSus && field(env, "external_corners_lm").value != null) {
    add("corners_suspect", "warn", "The external corner count doesn't look right (" + txt(String(extSus), 200).replace(/\.$/, "") +
      "). Check every corner on the corner picture before you quote.");
  }
  if (field(env, "external_corners_lm").value == null) {
    var extM = String(field(env, "external_corners_lm").method || "").match(/not measured on ([^(]+)\(/);
    add("no_externals", "info", multiMeasured
      ? "External corners couldn't be counted on " + (extM ? txt(extM[1].trim(), 80) : "every floor") + ", so there's no external corner total for the house."
      : "External corners couldn't be counted from the drawing — that number is the AI's estimate.");
  }
  var needs = env && Array.isArray(env.needs_confirmation) ? env.needs_confirmation : [];
  if (needs.some(function (n) { return n && n.field === "scale"; })) {
    add("scale_unchecked", "info", "The drawing scale couldn't be double-checked against a printed area. Check one room's size against the plan.");
  }
  // ── HOLD: a complete measurement of an alteration (alter-v2) ─────────────
  // Every other check has run (and its warnings stay in the reasons). Only a
  // measurement from the drawing itself is "the whole building"; the printed
  // room sizes (v1) are a read of it, not a measurement.
  if (isAlter) {
    var traced = geo.kind !== "v1";
    add("alteration", "hold", "This plan is an alteration of an existing building (" + altSaid + "), so only part of it is new work. " +
      (traced
        ? (weakPartial ? "PlanBoss measured the building it could trace" : "PlanBoss measured the whole building") +
          ((gFloor || gFoot) ? " — about " + r0(gFloor || gFoot) + " m² of floor —" : "")
        : "PlanBoss could only read the room sizes printed on the plan for the whole building (" + engineWhy(geo.note).replace(/\.$/, "") + ")") +
      " and quoting from that would price walls and ceilings nobody is touching. Take off just the rooms being worked on." + workNote);
    return out("hold", "alteration", "This plan is an alteration", traced);
  }
  var warns = reasons.filter(function (x) { return x.level === "warn"; }).length;
  if (warns) return out("check", "check", "Measured from the drawing — check " + (warns === 1 ? "one thing" : warns + " things") + " before you quote");
  return out("ok", null, "Measured from the drawing");
}

module.exports = {
  VERSION: VERSION,
  planVerdict: planVerdict,
  classifyGeometry: classifyGeometry,
  engineWhy: engineWhy,
  alterationSaid: alterationSaid,
};
